<template>
  <div v-show="showSiteHeader">
    <div v-if="!isStatic" class="spacer" :style="{ height: `${offsetTop}px` }"></div>

    <div ref="rootEl" class="site-header" :class="[siteHeaderMode, { dock, float }]">
      <header ref="headerEl">
        <div class="site-header-content">
          <NavLink :href="rootUrl">
            <img class="logo" src="app/assets/images/logo.png" alt="TourHero Logo" />
          </NavLink>

          <HeaderLinks v-if="desktop" :float />

          <HeaderMenu :float @logout="logout()" />
        </div>
      </header>

      <div id="site-header-bottom"></div>
    </div>
  </div>
</template>

<script setup>
import api from '@/utils/api.js'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useElementBounding, useStyleTag, useTimeoutFn } from '@vueuse/core'
import { useBodyClassWhen, useShared } from '@/helpers/vueHelpers.js'
import { useSiteHeaderStore } from '@/stores/siteHeader.js'
import { useWindowScroll } from '@vueuse/core'
import { metaTagContent } from '@/helpers/documentHelpers.js'
import HeaderLinks from '@/modules/Header/HeaderLinks.vue'
import HeaderMenu from '@/modules/Header/HeaderMenu.vue'
import { useGlobalState } from '@/stores/global.js'
import { setBodyCssVar } from '@/helpers/vueHelpers.js'
import NavLink from '@/components/NavLink.vue'

const rootEl = ref(null)
const headerEl = ref(null)
const dock = ref(true)
const float = ref(false)
const { y } = useWindowScroll()
const { height: fullHeight } = useElementBounding(rootEl, { updateTiming: 'next-frame' })
const { bottom, height, update: updateBounding } = useElementBounding(headerEl, { updateTiming: 'next-frame' })
const dockHeight = ref(0)
const rootUrl = metaTagContent('menuRootUrl')
const logoutUrl = metaTagContent('menuLogoutUrl')
const { css } = useStyleTag()
const { desktop } = useShared()
const { forceShow } = storeToRefs(useSiteHeaderStore())
const { showSiteHeader, siteHeaderHeight, siteHeaderBottom, siteHeaderMode } = useGlobalState()
const isStatic = computed(() => siteHeaderMode.value === 'static')
const offsetTop = computed(() => Math.max(fullHeight.value, dockHeight.value))

watch([y, forceShow, isStatic], ([newY, show], [oldY]) => {
  if (isStatic.value) {
    dock.value = true
    float.value = false
    return
  }

  dock.value = newY <= dockHeight.value
  float.value = !dock.value && (newY <= oldY || show)
})

watch([dock, height], ([dock, height]) => {
  dockHeight.value = dock ? height : dockHeight.value
  css.value = `.flash { margin-top: ${dockHeight.value}px; }`
})

watch(height, val => {
  siteHeaderHeight.value = Math.round(val)
  setBodyCssVar('--site-header-height', `${siteHeaderHeight.value}px`)
})

watch(bottom, val => {
  siteHeaderBottom.value = Math.round(val)
  setBodyCssVar('--site-header-bottom', `${siteHeaderBottom.value}px`)
})

watch([dock, float], () => {
  // required because css transtion don't trigger bounding updates
  useTimeoutFn(updateBounding, 200) // match transform transition timing
})

useBodyClassWhen(dock, 'site-header--docked')
useBodyClassWhen(float, 'site-header--floating')

async function logout() {
  try {
    await api.delete(logoutUrl)
  } catch (err) {
    console.error(err)
  } finally {
    window.location.replace(rootUrl)
  }
}
</script>

<style lang="scss" scoped>
.site-header {
  z-index: 2900;
  top: 0;
  left: 0;
  right: 0;
  transform: translateY(calc(-1 * var(--site-header-height)));
  transition: transform 200ms linear;

  &.dynamic {
    position: fixed;
  }

  & > header {
    @include main-container;
    @include main-wrapper;
  }

  @include mobile {
    & > header {
      padding: 0;
      border-bottom: 2px solid #f6f6f6;
    }

    .site-header-content {
      padding: 10px 20px 10px 24px !important;
      border-radius: 0;
    }
  }
}

.site-header.dock {
  transform: translateY(0);

  & > header {
    border-bottom: 2px solid #f6f6f6;
    background-color: white;
  }

  .site-header-content {
    padding: 20px 0;
    box-shadow: none;
  }
}

.site-header.float {
  @include desktop {
    transform: translateY(15px);
  }

  @include mobile {
    transform: translateY(0);
  }
}

.site-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  box-shadow: 0 2px 12px 0 rgba(34, 34, 34, 0.1);

  .logo {
    height: 32px;
    // visually center
    position: relative;
    top: -4px;

    @include mobile {
      height: 24px;
      top: -2.5px;
    }
  }
}
</style>
